import React, { useRef } from "react";
import { Formik } from "formik";
import CardDetailLoader from "components/CardDetailLoader";
import { fetchFeatureGroupFeatures } from "actions/featureGroup";
import { fetchMerchantAccountProductsPrices } from "actions/merchantAccount";
import { bindActionCreators } from "redux";
import { FEATURE_GROUPS_SCHEMA } from "constants/validation";
import { connect } from "react-redux";
import { bulkUpdateFeatureGroupsEntitlements } from "actions/featureGroup";
import {
  getFeaturesFormInitialValues,
  bulkUpdateFeatureGroupsParams
} from "utils/feature";
import { hasAnyUpdates } from "utils/data";
import { getDefaultFeatureTablePreviewProductsLayout } from "components/FormerEditor/common";
import FeatureTableDragContainer from "components/FeaturesSection/FeatureTableDragContainer";
import { usePageBuilderContext } from "utils/context";
import { captureException } from "utils/error";
import { handleFormCb } from "utils/form";
import isEqual from "lodash/isEqual";
import {
  ensureEntitlementConfigs,
  ensureFeatureGroupFeatures,
  ensureProductsPrices
} from "utils/hooks";
import { useCtxReferences } from "utils/selectors";
import { fetchEntitlementConfigs } from "actions/entitlementConfig";

/**
 * ============================
 * Main feature table in Settings > Features
 * ============================
 */
const FeatureTablePreviewContainer = ({
  layout,
  actions,
  adjustProductLayout
}) => {
  const formRef = useRef();
  const { resource, resourceID, resourceUUID } = useCtxReferences();
  const pageContext = usePageBuilderContext();
  const tracking = pageContext && pageContext.tracking;
  const {
    features,
    featureGroups,
    loading: featureGroupFeaturesLoading
  } = ensureFeatureGroupFeatures({
    fetchFeatureGroupFeatures: actions.fetchFeatureGroupFeatures
  });

  const {
    loading: loadingEntitlements,
    entitlementConfigs
  } = ensureEntitlementConfigs({
    fetchEntitlementConfigs: actions.fetchEntitlementConfigs
  });
  const commonEntitlementConfig = entitlementConfigs.find(
    ({ type }) => type === "common"
  );

  const {
    products,
    prices,
    loading: productsPricesLoading
  } = ensureProductsPrices({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices
  });

  const loading =
    featureGroupFeaturesLoading || productsPricesLoading || loadingEntitlements;
  if (loading) {
    return <CardDetailLoader />;
  }

  const productLayout =
    layout ||
    getDefaultFeatureTablePreviewProductsLayout({
      products,
      prices
    });

  const submitBulkUpdateFeatureGroupsEntitlements = (
    formValues,
    formikActions
  ) => {
    const data = bulkUpdateFeatureGroupsParams(
      formRef.current.initialValues,
      formValues
    );
    const hasFeaturesUpdates = hasAnyUpdates(data);
    const initialEntitlements = formRef.current.initialValues.entitlements;
    const currentEntitlements = formValues.entitlements;
    const sameEntitlements = isEqual(initialEntitlements, currentEntitlements);
    const hasEntitlementsUpdates = !sameEntitlements;

    if (hasFeaturesUpdates || hasEntitlementsUpdates) {
      const requestInput = {
        resourceID,
        resourceUUID,
        resource
      };

      if (hasFeaturesUpdates) {
        requestInput.featureGroups = data;
      }
      if (hasEntitlementsUpdates) {
        requestInput.entitlementConfig = {
          id: commonEntitlementConfig.id,
          uuid: commonEntitlementConfig.uuid,
          data: currentEntitlements
        };
      }

      actions.bulkUpdateFeatureGroupsEntitlements(requestInput, (err) =>
        handleFormCb(formValues, err, formikActions)
      );
    } else {
      formikActions.setSubmitting(false);
      formikActions.resetForm({ values: formValues });
      captureException(
        new Error("FeatureTablePreviewContainer: No updates to be made")
      );
    }
  };

  const initialValues = getFeaturesFormInitialValues({
    form: {
      adjustProductLayout: Boolean(adjustProductLayout)
    },
    features,
    featureGroups,
    productLayout,
    entitlements: commonEntitlementConfig ? commonEntitlementConfig.data : {}
  });

  return (
    <Formik
      innerRef={formRef}
      validationSchema={FEATURE_GROUPS_SCHEMA}
      initialValues={initialValues}
      onSubmit={submitBulkUpdateFeatureGroupsEntitlements}
    >
      {() => (
        <FeatureTableDragContainer products={products} tracking={tracking} />
      )}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      bulkUpdateFeatureGroupsEntitlements,
      fetchFeatureGroupFeatures,
      fetchEntitlementConfigs,
      fetchMerchantAccountProductsPrices
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(FeatureTablePreviewContainer);
